import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, Table, Tag, Tabs } from 'antd';
import { FileTextOutlined, UploadOutlined, PhoneOutlined, ArrowUpOutlined, ArrowDownOutlined, MinusOutlined } from '@ant-design/icons';
import { Line, Column } from '@ant-design/charts';
import { employeeService } from '../../../services';
import { formatMexicoTime, calculateMexicoWeekStats } from '../../../utils/timeUtils';
import type { EmployeeDashboard, Dataset, ManualEntryStats } from '../../../types';

const EmployeeDashboardPage: React.FC = () => {
  const [data, setData] = useState<EmployeeDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [manualStats, setManualStats] = useState<ManualEntryStats | null>(null);
  const [uploadTasks, setUploadTasks] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<'overview' | 'weekly' | 'monthly'>('overview');
  const [dashboardKey, setDashboardKey] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [dashboardResponse, statsResponse, uploadsResponse] = await Promise.all([
          employeeService.getDashboard(),
          employeeService.getManualEntryStats(),
          employeeService.getMyUploads(),
        ]);
        
        setData(dashboardResponse);
        setManualStats(statsResponse);
        setUploadTasks(uploadsResponse.upload_tasks || []);
      } catch (error) {
        console.error('获取仪表板数据失败:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // 计算批量导入的周度统计
  const getUploadTaskWeeklyStats = () => {
    const now = new Date();
    const currentWeekStart = new Date(now);
    currentWeekStart.setDate(now.getDate() - now.getDay() + 1); // 本周一
    currentWeekStart.setHours(0, 0, 0, 0);
    
    const lastWeekStart = new Date(currentWeekStart);
    lastWeekStart.setDate(currentWeekStart.getDate() - 7); // 上周一
    
    const lastWeekEnd = new Date(lastWeekStart);
    lastWeekEnd.setDate(lastWeekStart.getDate() + 6); // 上周日
    lastWeekEnd.setHours(23, 59, 59, 999);
    
    const currentWeekEnd = new Date(currentWeekStart);
    currentWeekEnd.setDate(currentWeekStart.getDate() + 6); // 本周日
    currentWeekEnd.setHours(23, 59, 59, 999);

    // 计算本周批量导入数据
    const currentWeekUploads = uploadTasks.filter(task => {
      const taskDate = new Date(task.created_at);
      return taskDate >= currentWeekStart && taskDate <= currentWeekEnd && task.status === 'completed';
    });

    // 计算上周批量导入数据
    const lastWeekUploads = uploadTasks.filter(task => {
      const taskDate = new Date(task.created_at);
      return taskDate >= lastWeekStart && taskDate <= lastWeekEnd && task.status === 'completed';
    });

    return {
      currentWeek: currentWeekUploads.reduce((sum, task) => sum + (task.new_phones_added || 0), 0),
      lastWeek: lastWeekUploads.reduce((sum, task) => sum + (task.new_phones_added || 0), 0),
    };
  };

  // 计算每日批量导入数据
  const getDailyUploadStats = () => {
    return calculateMexicoWeekStats(uploadTasks);
  };

  // 生成周统计图表数据（显示总数：手动+批量）
  const getWeeklyChartData = () => {
    if (!manualStats) return [];
    
    const currentWeekData = manualStats.current_week.daily_data;
    const lastWeekData = manualStats.last_week.daily_data;
    const { currentWeekDaily, lastWeekDaily } = getDailyUploadStats();

    // 分别构建上周和本周的数据，确保顺序一致
    const lastWeekDataPoints = [];
    const currentWeekDataPoints = [];
    
    for (let i = 0; i < 7; i++) {
      const currentDay = currentWeekData[i];
      const lastDay = lastWeekData[i];
      
      lastWeekDataPoints.push({
        day: lastDay.day_name,
        week: '上周',
        count: (lastDay.count || 0) + lastWeekDaily[i],
      });
      
      currentWeekDataPoints.push({
        day: currentDay.day_name,
        week: '本周',
        count: (currentDay.count || 0) + currentWeekDaily[i],
      });
    }
    
    // 返回合并后的数据
    return [...currentWeekDataPoints, ...lastWeekDataPoints];
  };

  // 生成月度统计图表数据（合并手动入库和批量导入）
  const getMonthlyChartData = () => {
    if (!manualStats) return [];
    
    const uploadStats = getUploadTaskWeeklyStats();
    
    // 使用周度数据生成月度统计
    const data: Array<{
      week: string;
      manual: number;
      upload: number;
      total: number;
    }> = [];
    
    data.push({
      week: '本周',
      manual: manualStats.current_week.total,
      upload: uploadStats.currentWeek,
      total: manualStats.current_week.total + uploadStats.currentWeek,
    });
    
    data.push({
      week: '上周',
      manual: manualStats.last_week.total,
      upload: uploadStats.lastWeek,
      total: manualStats.last_week.total + uploadStats.lastWeek,
    });
    
    return data;
  };

  const weeklyChartConfig = {
    data: getWeeklyChartData(),
    xField: 'day',
    yField: 'count',
    seriesField: 'week',
    smooth: true,
    point: {
      size: 5,
      shape: 'diamond',
    },
    legend: {
      position: 'top' as const,
    },
    yAxis: {
      min: 0,
      tickCount: 5,
    },
    // 使用对象形式配置颜色映射
    colorField: 'week',
    colorMapping: {
      '上周': '#1890ff',  // 蓝色
      '本周': '#52c41a',  // 绿色
    },
  };

  const monthlyChartConfig = {
    data: getMonthlyChartData(),
    xField: 'week',
    yField: 'total',
    columnStyle: {
      radius: [4, 4, 0, 0],
    },
    color: '#52c41a',
  };

  const columns = [
    {
      title: '数据集名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      ellipsis: true,
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      ellipsis: true,
    },
    {
      title: '创建者',
      dataIndex: 'creator',
      key: 'creator',
      width: 100,
    },
    {
      title: '记录数量',
      dataIndex: 'phone_count',
      key: 'phone_count',
      width: 100,
      render: (count: number) => <Tag color="blue">{count}</Tag>,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => formatMexicoTime(date),
    },
  ];

  return (
    <div>
      <h1>员工仪表板</h1>
      
      {/* 基础统计卡片 */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="可用数据集"
              value={data?.datasets?.length || 0}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="总入库数"
              value={data?.my_uploads || 0}
              prefix={<UploadOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="本周总入库"
              value={(manualStats?.current_week?.total || 0) + getUploadTaskWeeklyStats().currentWeek}
              prefix={<PhoneOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* 统计图表标签页 */}
      <Card key={dashboardKey} style={{ marginBottom: 24 }}>
        <Tabs
          activeKey={activeTab}
          onChange={(key) => {
            setActiveTab(key as any);
            setDashboardKey(prev => prev + 1);
          }}
          items={[
            {
              key: 'overview',
              label: '概览',
              children: (
                <Card title="可用数据集">
                  <Table
                    columns={columns}
                    dataSource={data?.datasets || []}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 730 }}
                    pagination={{ pageSize: 10 }}
                  />
                </Card>
              ),
            },
            {
              key: 'weekly',
              label: '周度统计',
              children: manualStats ? (
                <div>
                  {(() => {
                    const uploadStats = getUploadTaskWeeklyStats();
                    const currentWeekTotal = manualStats.current_week.total + uploadStats.currentWeek;
                    const lastWeekTotal = manualStats.last_week.total + uploadStats.lastWeek;
                    const change = currentWeekTotal - lastWeekTotal;
                    const changePercent = lastWeekTotal > 0 ? (change / lastWeekTotal) * 100 : 0;
                    
                    return (
                      <>
                        
                        <Card title="周度入库趋势对比（手动+批量）">
                          <Line {...weeklyChartConfig} />
                        </Card>
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 50 }}>
                  <p>暂无统计数据</p>
                </div>
              ),
            },
            {
              key: 'monthly',
              label: '周度对比',
              children: (
                <div>
                  <Card title="本周与上周入库对比">
                    <Column {...monthlyChartConfig} />
                  </Card>
                </div>
              ),
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default EmployeeDashboardPage;
